import * as THREE from "three";

export class SectorPosition {
  readonly sector = new THREE.Vector3();
  readonly local = new THREE.Vector3();

  constructor(
    sector: THREE.Vector3 | readonly [number, number, number] = [0, 0, 0],
    local: THREE.Vector3 | readonly [number, number, number] = [0, 0, 0],
    readonly sectorSize = 100_000,
  ) {
    this.sector.copy(sector instanceof THREE.Vector3 ? sector : new THREE.Vector3(...sector));
    this.local.copy(local instanceof THREE.Vector3 ? local : new THREE.Vector3(...local));
    this.normalize();
  }

  clone(): SectorPosition {
    return new SectorPosition(this.sector, this.local, this.sectorSize);
  }

  normalize(): this {
    const half = this.sectorSize * 0.5;
    for (const axis of ["x", "y", "z"] as const) {
      const value = this.local[axis];
      const shift = Math.floor((value + half) / this.sectorSize);
      if (shift !== 0) {
        this.sector[axis] += shift;
        this.local[axis] -= shift * this.sectorSize;
      }
    }
    return this;
  }

  translate(delta: THREE.Vector3): this {
    this.local.add(delta);
    return this.normalize();
  }

  relativeTo(observer: SectorPosition, target = new THREE.Vector3()): THREE.Vector3 {
    target.set(
      (this.sector.x - observer.sector.x) * this.sectorSize + this.local.x - observer.local.x,
      (this.sector.y - observer.sector.y) * this.sectorSize + this.local.y - observer.local.y,
      (this.sector.z - observer.sector.z) * this.sectorSize + this.local.z - observer.local.z,
    );
    return target;
  }

  wire(): { sector: [number, number, number]; local: [number, number, number] } {
    return {
      sector: [Math.trunc(this.sector.x), Math.trunc(this.sector.y), Math.trunc(this.sector.z)],
      local: [this.local.x, this.local.y, this.local.z],
    };
  }
}

export class FloatingOrigin {
  readonly position: SectorPosition;

  constructor(sectorSize: number, sector: readonly [number, number, number], local: readonly [number, number, number]) {
    this.position = new SectorPosition(sector, local, sectorSize);
  }
}
